import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AlertService } from 'app/core-lib/services/alerts/alert.service';
import { ErrorCodes } from 'app/shared/errors.enums';
import { SharedModule } from 'app/shared/shared.module';
import { matchPasswordValidator } from 'app/shared/validators/match-password.validator';
import { strengthValidator } from 'app/shared/validators/strength-password.validator';
import { AuthFormHeaderComponent } from '../auth-form-header/auth-form-header.component';
import { ChangePasswordResponse, RecoveryService, RecoveryUser } from '../recovery/recovery.service';

const EVERY_RULE_FAILED = {
  hasUpperCase: true,
  hasLowerCase: true,
  hasNumber: true,
  hasSpecialCharacters: true,
  hasValidLength: true,
  exception: true,
};

const emptyFailsEveryRule: ValidatorFn = (control) => (control.value ? null : { passwordStrength: EVERY_RULE_FAILED });

const PASSWORD_REUSE_KEY = 'set_password.password_reuse';

const isPasswordReuse = (response: ChangePasswordResponse): boolean =>
  response?.code === ErrorCodes.PASSWORD_REUSE || !!response?.details?.includes(String(ErrorCodes.PASSWORD_REUSE));

@Component({
  selector: 'mh-set-password-form',
  standalone: true,
  imports: [SharedModule, ReactiveFormsModule, AuthFormHeaderComponent],
  templateUrl: './set-password-form.component.html',
  styleUrls: ['./set-password-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'tw-block' },
})
export class SetPasswordFormComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly recoveryService = inject(RecoveryService);
  private readonly alertService = inject(AlertService);
  private readonly destroyRef = inject(DestroyRef);

  readonly titleKey = input.required<string>();
  readonly explainKey = input.required<string>();
  readonly token = input.required<string>();
  readonly user = input.required<RecoveryUser>();

  readonly possibleErrors = Object.keys(EVERY_RULE_FAILED);

  readonly newPassword = new FormControl('', [Validators.required, strengthValidator(), emptyFailsEveryRule]);
  readonly confirmPassword = new FormControl('', [Validators.required, matchPasswordValidator(this.newPassword)]);
  readonly form = new FormGroup({ new_password: this.newPassword, confirm_password: this.confirmPassword });

  readonly saving = signal(false);
  readonly serverErrorKey = signal<string | null>(null);
  readonly showNewPassword = signal(false);
  readonly showConfirmPassword = signal(false);

  private readonly formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });
  readonly canSubmit = computed(() => this.formStatus() === 'VALID' && !this.saving());
  readonly newPasswordClass = computed(() =>
    this.serverErrorKey() === PASSWORD_REUSE_KEY ? 'mh-auth-input mh-auth-input--error' : 'mh-auth-input',
  );

  ngOnInit(): void {
    this.newPassword.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.serverErrorKey.set(null);
      this.confirmPassword.updateValueAndValidity({ emitEvent: false });
    });
  }

  submit(): void {
    if (!this.canSubmit()) return;

    this.saving.set(true);
    this.serverErrorKey.set(null);
    this.recoveryService
      .changePassword(this.token(), this.user(), this.newPassword.value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response?.code !== 200) return this.handleChangeError(response);

          this.notifySuccess();
          void this.router.navigate(['/login']);
        },
        error: (error) => this.handleChangeError(error),
      });
  }

  private handleChangeError(response: ChangePasswordResponse): void {
    this.serverErrorKey.set(isPasswordReuse(response) ? PASSWORD_REUSE_KEY : 'errors.login.something_went_wrong');
    this.saving.set(false);
  }

  private notifySuccess(): void {
    this.alertService.handleAlert(
      'success.update_login_password',
      5000,
      'top',
      'center',
      'snackbar-panel-success-user-config',
    );
  }
}
